/**
 * Presentation helpers. All money arithmetic stays in integer minor units and
 * is delegated to the shared formatters — nothing here does float maths on
 * money.
 */

import { formatDistanceToNowStrict } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { MICRO_PER_USDT, formatRub, formatUsdt } from '@shared/types';

/** Narrow no-break space: keeps the amount and its symbol on one line. */
const NBSP = ' ';

/** 123456 kopecks -> "1 234,56 ₽". The symbol follows the figure in Russian. */
export function rub(kopecks: number): string {
  return `${formatRub(kopecks)}${NBSP}₽`;
}

/** 1234560 micro-USDT -> "1,23 USDT" */
export function usdt(micro: number): string {
  return `${formatUsdt(micro)}${NBSP}USDT`;
}

/**
 * The deadline countdown, in the units a Russian reader expects: «4 мин 12 с»
 * rather than a bare «4:12». Terse on purpose — this string is redrawn every
 * second and is the most-read text in the product.
 */
export function clock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  if (hours > 0) {
    return `${hours}${NBSP}ч ${String(minutes).padStart(2, '0')}${NBSP}мин`;
  }
  if (minutes > 0) {
    return `${minutes}${NBSP}мин ${String(seconds).padStart(2, '0')}${NBSP}с`;
  }
  return `${seconds}${NBSP}с`;
}

/** Below this, "12 секунд назад" is noise — the event is simply just now. */
const JUST_NOW_SECONDS = 10;
const WEEK_SECONDS = 604_800;

/**
 * «12 минут назад». date-fns owns the inflection: Russian needs «1 минуту»,
 * «2 минуты» and «5 минут», which no amount of hand-rolled suffixing gets
 * right. Falls back to an absolute date beyond a week.
 */
export function relativeTime(iso: string): string {
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return '—';

  const deltaSeconds = Math.abs(timestamp - Date.now()) / 1000;
  if (deltaSeconds < JUST_NOW_SECONDS) return 'только что';
  if (deltaSeconds >= WEEK_SECONDS) return absoluteDate(iso);

  return formatDistanceToNowStrict(timestamp, { addSuffix: true, locale: ru });
}

/** "28.08.2026" — the Russian short date. */
export function absoluteDate(iso: string): string {
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return '—';
  return new Date(timestamp).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** "28.08.2026 14:35" — 24-hour clock, as Russian dates are always written. */
export function absoluteDateTime(iso: string): string {
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return '—';
  return `${absoluteDate(iso)} ${new Date(timestamp).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** "•••• 4242" — the only card representation the UI ever renders. */
export function maskedCard(last4: string | null): string {
  return last4 ? `•••• ${last4}` : '—';
}

/** Group a PAN in fours while the trader types. Digits only. */
export function groupCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19);
  const groups = digits.match(/.{1,4}/g);
  return groups ? groups.join(' ') : '';
}

/**
 * Luhn check digit validation, done entirely in the browser. The PAN itself is
 * never transmitted — only the last four digits are sent to the server.
 */
export function isValidLuhn(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  // "0000 0000 0000 0000" satisfies Luhn but is never a real card.
  if (/^(\d)\1+$/.test(digits)) return false;

  let sum = 0;
  let shouldDouble = false;

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (digit < 0 || digit > 9) return false;

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

export function last4Of(raw: string): string {
  return raw.replace(/\D/g, '').slice(-4);
}

const BYTES_PER_MB = 1024 * 1024;

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}${NBSP}Б`;
  if (bytes < BYTES_PER_MB) return `${Math.round(bytes / 1024)}${NBSP}КБ`;
  return `${(bytes / BYTES_PER_MB).toFixed(1).replace('.', ',')}${NBSP}МБ`;
}

/**
 * A signed ledger amount: «+1,23 USDT» or «−1,23 USDT».
 *
 * The minus is U+2212, not a hyphen: at the mono sizes used in the statement a
 * hyphen is barely a pixel taller than the decimal comma. The sign is the only
 * thing separating a credit from a debit, so it is never carried by colour and
 * every row that renders one also names the direction for a screen reader.
 */
export function signedUsdt(micro: number): string {
  const sign = micro < 0 ? '−' : '+';
  return `${sign}${formatUsdt(Math.abs(micro))}${NBSP}USDT`;
}

/** «12 %» — a whole-percent share, for usage meters. Never money. */
export function percent(ratio: number): string {
  const clamped = Math.max(0, Math.min(1, ratio));
  return `${Math.round(clamped * 100)}${NBSP}%`;
}

/**
 * The USDT counterpart of the shared `parseRubToKopecks`, and deliberately as
 * forgiving: a trader typing an amount back the way the interface prints it
 * («1 234,56», narrow spaces and all) must not get NaN. Returns integer
 * micro-USDT, or NaN when the text is not a number.
 *
 * Money never becomes a float beyond this one rounding step — everything
 * downstream, including the request body, is integer micro.
 */
export function parseUsdtToMicro(value: string): number {
  const normalised = value.replace(/[\s   ]/g, '').replace(',', '.');
  if (normalised === '') return Number.NaN;
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? Math.round(parsed * MICRO_PER_USDT) : Number.NaN;
}
